import type { ChannelType } from './constants'
import { BRT_OFFSET_MINUTES } from './period'

/**
 * Gatilhos de mensagem — o que o contato faz no Telegram.
 *
 * - `first_inbound`: a primeira mensagem que o lead manda **nesta conversa**.
 * - `start_command`: o `/start` de um deep link, com ou sem campanha.
 * - `palavra_chave`: o texto recebido casa com uma das palavras.
 * - `fora_do_horario`: chegou mensagem fora do expediente do inbox.
 */
export const GATILHOS_DE_MENSAGEM = [
  'first_inbound',
  'start_command',
  'palavra_chave',
  'fora_do_horario',
] as const

/**
 * Gatilhos de evento do contato — o que acontece **com** o contato, no CRM.
 *
 * Entram por outra porta do motor: não vêm de mensagem recebida, vêm dos
 * serviços de ação. É a diferença entre "ele escreveu" e "alguém (ou um fluxo)
 * mexeu no cadastro dele".
 *
 * `contato_criado` não é a mesma coisa que `first_inbound`, e ter os dois é
 * proposital: quem entra por importação de CSV dispara o primeiro e nunca o
 * segundo, porque nunca escreveu nada. `first_inbound` é por conversa e por
 * canal; `contato_criado` é por pessoa, uma vez na vida.
 */
export const GATILHOS_DE_EVENTO = [
  'contato_criado',
  'tag_aplicada',
  'tag_removida',
  'campo_alterado',
  'etapa_alterada',
  'responsavel_alterado',
  'link_clicado',
] as const

/**
 * Gatilhos de tempo — o que **não** aconteceu por um tempo.
 *
 * Nenhum evento os anuncia: "ficou dois dias em Aguardando" é justamente a
 * ausência de mudança. Quem os percebe é a varredura de minuto em minuto, por
 * isso a precisão é de um minuto.
 */
export const GATILHOS_DE_TEMPO = ['tempo_na_etapa'] as const

export const AUTOMATION_TRIGGERS = [
  ...GATILHOS_DE_MENSAGEM,
  ...GATILHOS_DE_EVENTO,
  ...GATILHOS_DE_TEMPO,
] as const
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number]
export type GatilhoDeEvento = (typeof GATILHOS_DE_EVENTO)[number]

/** O gatilho é acionado por evento de cadastro, e não por mensagem? */
export function ehGatilhoDeEvento(gatilho: AutomationTrigger): gatilho is GatilhoDeEvento {
  return (GATILHOS_DE_EVENTO as readonly string[]).includes(gatilho)
}

/**
 * Teto de encadeamento entre fluxos.
 *
 * Um fluxo que aplica tag pode disparar outro que dispara por tag — e isso é
 * um recurso, não um defeito: é como se encadeia qualificação. O que não pode é
 * girar. Cada execução guarda a própria profundidade, e o evento que ela gera
 * nasce um nível abaixo; passou do teto, não começa mais nada.
 *
 * Três níveis porque é o que cobre o encadeamento real que alguém monta à mão
 * (qualifica → segmenta → responde) sem deixar espaço para o laço passar
 * despercebido.
 */
export const MAX_PROFUNDIDADE_DE_FLUXO = 3

/** Como comparar o texto recebido com as palavras da regra. */
export const AUTOMATION_MATCH_MODES = ['contem', 'exata'] as const
export type AutomationMatchMode = (typeof AUTOMATION_MATCH_MODES)[number]

export const AUTOMACAO_MAX_PALAVRAS = 20
export const AUTOMACAO_MAX_BOTOES = 6

/**
 * O que cada tipo de canal aceita.
 *
 * Fonte única: o servidor valida por aqui e a tela se monta por aqui. Sem isso
 * a regra "business não tem botão" vira dois lugares que discordam — um libera
 * o campo e o outro recusa o salvamento, sem dizer por quê.
 */
export interface CapacidadesDoCanal {
  /** Teclado inline. O Telegram recusa `reply_markup` em nome de conta business. */
  botoes: boolean
  /** `t.me/<bot>?start=<payload>` — só bot tem link de entrada próprio. */
  deepLink: boolean
  /** Menu de comandos do Telegram (`setMyCommands`). */
  comandos: boolean
  /** Gatilhos que fazem sentido neste canal. */
  gatilhos: readonly AutomationTrigger[]
}

const CAPACIDADES: Record<ChannelType, CapacidadesDoCanal> = {
  bot: {
    botoes: true,
    deepLink: true,
    comandos: true,
    gatilhos: AUTOMATION_TRIGGERS,
  },
  /**
   * Conta business só sabe ler texto e responder texto.
   *
   * Não é limitação do CRM: mensagem enviada em nome de uma conta business não
   * aceita teclado, não tem `/start` (a conversa começa no app do Telegram, sem
   * deep link) e não tem menu de comandos. Um menu aqui é "digite 1, 2 ou 3".
   */
  business: {
    botoes: false,
    deepLink: false,
    comandos: false,
    /**
     * Os eventos de contato valem nos dois — são do CRM, não do Telegram, e
     * acontecem igual em quem chegou por qualquer caminho. Duas exceções, e as
     * duas pelo mesmo motivo: dependem de algo que conta business não tem.
     *
     * O `/start` depende de link de entrada. O `link_clicado` depende de botão
     * de link, e botão é justamente o que o Telegram recusa em nome de conta
     * business — o gatilho existiria na lista e nunca dispararia.
     */
    gatilhos: [
      'first_inbound',
      'palavra_chave',
      'fora_do_horario',
      ...GATILHOS_DE_EVENTO.filter((gatilho) => gatilho !== 'link_clicado'),
      ...GATILHOS_DE_TEMPO,
    ],
  },
}

export function capacidadesDoCanal(tipo: ChannelType): CapacidadesDoCanal {
  return CAPACIDADES[tipo]
}

/** O gatilho é oferecido neste canal? */
export function gatilhoCabeNoCanal(tipo: ChannelType, gatilho: AutomationTrigger): boolean {
  return CAPACIDADES[tipo].gatilhos.includes(gatilho)
}

/**
 * Uma faixa de atendimento num dia da semana.
 *
 * `fim` menor ou igual a `inicio` significa que a faixa atravessa a meia-noite:
 * 18:00–02:00 é o plantão que termina na madrugada seguinte, e não uma faixa
 * vazia. Escrever isso como duas faixas obrigaria quem configura a pensar em
 * dois dias para um turno só.
 */
export interface FaixaDoDia {
  ativo: boolean
  /** `HH:MM` no fuso de Brasília. */
  inicio: string
  fim: string
}

/** Sete posições, índice 0 = domingo — a mesma ordem do `getDay()`. */
export type Expediente = FaixaDoDia[]

const FECHADO: FaixaDoDia = { ativo: false, inicio: '09:00', fim: '18:00' }
const COMERCIAL: FaixaDoDia = { ativo: true, inicio: '09:00', fim: '18:00' }

/** Segunda a sexta, 9h às 18h. */
export const EXPEDIENTE_PADRAO: Expediente = [
  FECHADO,
  COMERCIAL,
  COMERCIAL,
  COMERCIAL,
  COMERCIAL,
  COMERCIAL,
  FECHADO,
]

/** `HH:MM` em minutos desde a meia-noite, ou `null` se não for um horário. */
export function minutosDoDia(hhmm: string): number | null {
  const casa = /^(\d{2}):(\d{2})$/.exec(hhmm.trim())
  if (!casa) return null
  const horas = Number(casa[1])
  const minutos = Number(casa[2])
  if (horas > 23 || minutos > 59) return null
  return horas * 60 + minutos
}

export function expedienteValido(expediente: Expediente): boolean {
  if (expediente.length !== 7) return false
  return expediente.every(
    (faixa) => minutosDoDia(faixa.inicio) !== null && minutosDoDia(faixa.fim) !== null,
  )
}

/** Dia da semana (0=domingo) e minuto do dia, em Brasília. */
function agoraEmBrasilia(instante: Date): { dia: number; minuto: number } {
  const local = new Date(instante.getTime() + BRT_OFFSET_MINUTES * 60_000)
  return {
    dia: local.getUTCDay(),
    minuto: local.getUTCHours() * 60 + local.getUTCMinutes(),
  }
}

function cobre(faixa: FaixaDoDia, minuto: number, atravessando: boolean): boolean {
  if (!faixa.ativo) return false
  const inicio = minutosDoDia(faixa.inicio)
  const fim = minutosDoDia(faixa.fim)
  if (inicio === null || fim === null) return false

  // Faixa normal (09:00–18:00): só interessa o dia dela.
  if (fim > inicio) return !atravessando && minuto >= inicio && minuto < fim
  // Faixa que vira o dia (18:00–02:00): antes da meia-noite conta no próprio
  // dia, depois dela conta na madrugada do dia seguinte.
  return atravessando ? minuto < fim : minuto >= inicio
}

/**
 * O instante cai dentro do expediente?
 *
 * Checa o dia corrente e também o anterior, porque um turno que atravessa a
 * meia-noite pertence ao dia em que começou: 01h de terça ainda é o plantão de
 * segunda, e olhar só para terça diria "fechado" no meio do atendimento.
 */
export function dentroDoExpediente(expediente: Expediente, instante: Date): boolean {
  if (expediente.length !== 7) return true

  const { dia, minuto } = agoraEmBrasilia(instante)
  const hoje = expediente[dia]
  const ontem = expediente[(dia + 6) % 7]

  if (hoje && cobre(hoje, minuto, false)) return true
  if (ontem && cobre(ontem, minuto, true)) return true
  return false
}

/** Minúsculas e sem acento — "Preço", "preco" e "PREÇO" são a mesma palavra. */
export function normalizarTexto(texto: string): string {
  return (
    texto
      .normalize('NFD')
      // Faixa dos diacríticos combinantes, escrita como escape: o caractere
      // literal é invisível e cada editor normaliza o arquivo de um jeito.
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR')
      .trim()
  )
}

/**
 * O texto recebido casa com alguma palavra da regra?
 *
 * `exata` compara a mensagem inteira — serve para menu numérico ("1", "2"),
 * onde `contem` faria "10 reais" disparar a opção 1. `contem` procura a palavra
 * cercada por não-letras, e não como substring solta: sem isso "não" casaria
 * dentro de "informação" e a regra dispararia no meio de uma frase qualquer.
 */
export function casaPalavraChave(
  texto: string | null | undefined,
  palavras: string[],
  modo: AutomationMatchMode,
): boolean {
  if (!texto) return false
  const alvo = normalizarTexto(texto)
  if (!alvo) return false

  return palavras.some((bruta) => {
    const palavra = normalizarTexto(bruta)
    if (!palavra) return false
    if (modo === 'exata') return alvo === palavra
    const escapada = palavra.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(`(^|[^\\p{L}\\p{N}])${escapada}($|[^\\p{L}\\p{N}])`, 'u').test(alvo)
  })
}
